import {
	AccountLevelEnum,
	CarriersEnum,
	GroundEconomyPickUpCarrierEnum,
	RatingModeEnum,
	CarrierPaymentTypeEnum,
} from '@app/app.enum';
import store from '@app/app.store';
import * as models from '@app/models/index';
import injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { MetaDataService } from '@app/services/meta-data.service';
import { SystemConfigService } from '@app/services/system-config.service';
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	Grid,
	Link,
	MenuItem,
	TextField,
	Typography,
} from '@material-ui/core';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { IFedExNativeConfiguration } from '../../models/carrier.models';
import withSnackBar from '../core/message.component';
import { getFedExNativeConfigurationSchema } from './carriers.schema';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import CustomTooltip from '../shared/custom-tooltip.component';
import CarrierLogoNameComponent from './carrier-logo-name.component';
import Icon from '../shared/icon.component';
import RatingModeComponent from './rating-mode.component';

const FedExNativeConfigurationFormFactory: IFedExNativeConfiguration = {
	meterNumber: '',
	shipperAccountNumber: '',
	shipperGroundAccountNumber: '',
	billingAccountNumber: '',
	billingGroundAccountNumber: '',
	sequenceNumberStart: null,
	sequenceNumberEnd: null,
	sequenceNumber: null,
	isSmartPostEnabled: false,
	ratingMode: RatingModeEnum.WithShipment,
	smartPostId: null,
	smartPostHubId: null,
	smartPostDistributionCenter: null,
	smartPostMailerId: null,
	groundEconomyPickUpCarrier: GroundEconomyPickUpCarrierEnum.FedExGroundEconomy,
	alternateId: '',
};

let formValues: IFedExNativeConfiguration = null;

const accountService: AccountService = injector.get(AccountService);
const systemConfigService: SystemConfigService = injector.get(SystemConfigService);
const matadataService: MetaDataService = injector.get(MetaDataService);

const FedExConfigFormField = (props: IFormFieldsProps) => {
	return (
		<>
			<Field
				name={props.name}
				label={<FormattedMessage id={props.labelIntlKey ?? `app.carrier-${props.name}-label`} />}
				variant='outlined'
				component={TextField}
				type={props.type}
				fullWidth={true}
				required={!!props.required}
				disabled={!!props.disabled}
				value={props.formikProps.values[props.name] ?? ''}
				onChange={(e: React.ChangeEvent) => props.formikProps.handleChange(props.name)(e)}
				error={
					!!(
						(props.formikProps.values[props.name] || props.formikProps.submitCount) &&
						!!props.formikProps.errors[props.name]
					)
				}
				helperText={
					props.formikProps.values[props.name] || props.formikProps.submitCount
						? props.formikProps.errors[props.name]
						: null
				}
			/>
		</>
	);
};

FedExConfigFormField.defaultProps = {
	type: 'text',
};

const FedExNativeComponent: React.FunctionComponent<models.IFedExNativeProps> = forwardRef(
	(props, ref: React.MutableRefObject<any>) => {
		const formRef: React.MutableRefObject<Formik> = useRef(null);
		const history = useHistory();

		const [fedExNativeConfigurationSchema, setFedExNativeConfigurationSchema] = useState<any>(null);
		const [fedExNativeConfigurationForm, setFedExNativeConfigurationFormFactory] = useState<IFedExNativeConfiguration>(
			FedExNativeConfigurationFormFactory
		);
		const [isTrackingRangeFieldReadOnly, setIsTrackingRangeFieldReadOnly] = useState<boolean>(true);

		const isCustomer = useMemo(
			() => store.getState().authentication.user.account.level === AccountLevelEnum.Customer,
			[]
		);

		const IsAdmin = useMemo(() => store.getState().authentication.user.account.level === AccountLevelEnum.Admin, []);

		useEffect(() => {
			initializeForm();
			if (props.isTrackingRangeImported) {
				props.setIsTrackingRangeImported(false);
			}
		}, [props.isTrackingRangeImported]);

		useImperativeHandle(ref, () => ({
			getFormValues: async () => {
				try {
					await formRef.current.submitForm();
					const errors = Object.keys(formRef.current.state.errors).length;
					if (!errors) {
						return formValues;
					}
					return null;
				} catch (err) {
					throw err;
				}
			},
		}));

		const fedexNativeCarrierId = useMemo(async () => {
			const fedexNativeCarrier = await matadataService.getCarrierByCode(CarriersEnum.FedExNative);

			return fedexNativeCarrier.id;
		}, []);

		async function initializeForm() {
			try {
				const sequenceNumberRange = await systemConfigService.getFedExSequenceNumberRange();
				setFedExNativeConfigurationSchema(
					getFedExNativeConfigurationSchema(sequenceNumberRange, isCustomer, props.preloadedData)
				);
				if (props.preloadedData != null) {
					setFedExNativeConfigurationFormFactory((currentForm) => ({
						...currentForm,
						...props.preloadedData,
						isSmartPostEnabled: !!props.preloadedData.smartPostId,
						groundEconomyPickUpCarrier:
							props.preloadedData.groundEconomyPickUpCarrier ?? GroundEconomyPickUpCarrierEnum.FedExGroundEconomy,
						ratingMode: props.preloadedData.ratingMode ?? RatingModeEnum.WithShipment,
					}));

					try {
						const result = await accountService.GetFedExSequenceNumber({
							meterNumber: props.preloadedData.meterNumber,
							accountNumber: props.preloadedData.billingAccountNumber,
							groundAccountNumber: props.preloadedData.billingGroundAccountNumber,
						});
						setFedExNativeConfigurationFormFactory((currentForm) => ({
							...currentForm,
							...result,
						}));
					} catch (error) {}
				}
			} catch (error) {
				throw error;
			}
		}
		const isCustomerOrNewAccount = isCustomer || !props.preloadedData;

		return (
			<Grid container={true} justifyContent='center' alignItems='center'>
				{props.selectedCarrierLogo && (
					<CarrierLogoNameComponent name={props.selectedCarrierName} logo={props.selectedCarrierLogo} />
				)}
				<Grid item={true} sm={12} md={12} lg={12} xl={12} className='mt-2'>
					<Formik
						initialValues={fedExNativeConfigurationForm}
						onSubmit={() => {}}
						validationSchema={fedExNativeConfigurationSchema}
						enableReinitialize={true}
						ref={formRef}
					>
						{(formikProps: FormikProps<FormikValues>) => {
							// assigning form values to an outside variable
							formValues = formikProps.values as IFedExNativeConfiguration;
							return (
								<Form noValidate={true} className='width-100'>
									{/* Billing configs */}
									<Grid container={true} className='mt-2'>
										<Grid item={true} xs={12} className='mb-1' alignItems='center'>
											<Typography
												noWrap={true}
												color={'inherit'}
												variant='body1'
												className={classNames('display-inline-block')}
											>
												{props.intl.formatMessage({
													id: 'app.carrier-account.FedEx-Native-billing-config-section-header',
												})}
											</Typography>
										</Grid>
										<Grid container={true} justifyContent='flex-start' alignItems='flex-start' spacing={2}>
											<Grid item={true} xs={6}>
												<FedExConfigFormField name='meterNumber' required={true} formikProps={formikProps} />
											</Grid>
											<Grid item={true} xs={6}>
												<FedExConfigFormField
													name='billingAccountNumber'
													labelIntlKey='app.carrier-account-number-label'
													required={true}
													formikProps={formikProps}
												/>
											</Grid>
											<Grid item={true} xs={6}>
												<FedExConfigFormField
													name='billingGroundAccountNumber'
													labelIntlKey='app.carrier-ground-account-number-label'
													required={formikProps.values.isSmartPostEnabled}
													formikProps={formikProps}
												/>
											</Grid>
										</Grid>
									</Grid>

									{/* Shipper configs */}
									<Grid container={true} className='mt-2'>
										<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
											<Typography
												noWrap={true}
												color={'inherit'}
												variant='body1'
												className={classNames('display-inline-block')}
											>
												{props.intl.formatMessage({
													id: 'app.carrier-account.FedEx-Native-shipper-config-section-header',
												})}
											</Typography>
										</Grid>
										<Grid container={true} justifyContent='flex-start' alignItems='flex-start' spacing={2}>
											<Grid item={true} xs={6}>
												<FedExConfigFormField
													name='shipperAccountNumber'
													labelIntlKey='app.carrier-account-number-label'
													required={true}
													formikProps={formikProps}
												/>
											</Grid>
											<Grid item={true} xs={6}>
												<FedExConfigFormField
													name='shipperGroundAccountNumber'
													labelIntlKey='app.carrier-ground-account-number-label'
													required={formikProps.values.isSmartPostEnabled}
													formikProps={formikProps}
												/>
											</Grid>
										</Grid>
									</Grid>

									{/* Rating configs */}
									<Grid container={true} className='mt-2'>
										<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
											<Typography
												noWrap={true}
												color={'inherit'}
												variant='body1'
												className={classNames('display-inline-block')}
											>
												{props.intl.formatMessage({
													id: 'app.carrier-account.FedEx-Native-rating-config-section-header',
												})}
											</Typography>
											<CustomTooltip
												items={
													props.paymentType !== CarrierPaymentTypeEnum.Postpaid
														? JSON.parse(
																props.intl.formatMessage({
																	id: 'app.carrier-account.config.ratingMode-label-disabled.tooltip-message',
																})
														  )
														: JSON.parse(
																props.intl.formatMessage({
																	id: 'app.carrier-account.config.ratingMode-label-enabled.tooltip-message',
																})
														  )
												}
												className='display-inline-block ml-1'
											>
												<Typography>
													<Icon name='info-circle' />
												</Typography>
											</CustomTooltip>
										</Grid>

										<Grid item={true} xs={6} spacing={2}>
											<RatingModeComponent
												formikProps={formikProps}
												carrierCode={CarriersEnum.FedExNative}
												name='ratingMode'
												disabled={props.paymentType !== CarrierPaymentTypeEnum.Postpaid}
											/>
										</Grid>
									</Grid>

									{/* SmartPost® configs */}
									<Grid container={true} className='mt-2'>
										<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
											<Typography
												noWrap={true}
												color={'inherit'}
												variant='body1'
												className={classNames('display-inline-block')}
											>
												{props.intl.formatMessage({
													id: 'app.carrier-account.FedEx-Native-smart-post-config-section-header',
												})}
											</Typography>
										</Grid>
										<Grid container={true} justifyContent='flex-start' alignItems='flex-start' spacing={2}>
											<Grid item={true} xs={6}>
												<FormControlLabel
													control={
														<Checkbox
															name='isSmartPostEnabled'
															checked={formikProps.values.isSmartPostEnabled}
															onChange={(e) => {
																formikProps.setFieldValue('isSmartPostEnabled', e.target.checked);
															}}
														/>
													}
													label={props.intl.formatMessage({ id: 'app.carrier-smartpost-checkbox-label' })}
												/>
											</Grid>
											{formikProps.values.isSmartPostEnabled && (
												<>
													<Grid item={true} xs={6}>
														<FormControl fullWidth={true}>
															<TextField
																select={true}
																variant='outlined'
																label={props.intl.formatMessage({
																	id: 'app.carrier-ground-economy-pickup-carrier-label',
																})}
																required={true}
																fullWidth={true}
																disabled={false}
																InputLabelProps={{ shrink: true }}
																SelectProps={{
																	renderValue: (value: string) => {
																		return props.intl.formatMessage({
																			id: `app.carrier-ground-economy-pickup-${value}-carrier-label`,
																		});
																	},
																}}
																value={formikProps.values.groundEconomyPickUpCarrier}
																onChange={(e) => formikProps.handleChange('groundEconomyPickUpCarrier')(e)}
															>
																{Object.entries(GroundEconomyPickUpCarrierEnum).map(
																	([key, value]: [string, string], index: number) => (
																		<MenuItem key={index} value={value}>
																			{props.intl.formatMessage({
																				id: `app.carrier-ground-economy-pickup-${value}-carrier-label`,
																			})}
																		</MenuItem>
																	)
																)}
															</TextField>
														</FormControl>
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<FedExConfigFormField name='smartPostId' required={true} formikProps={formikProps} />
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<FedExConfigFormField name='smartPostHubId' required={true} formikProps={formikProps} />
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<FedExConfigFormField name='smartPostMailerId' required={true} formikProps={formikProps} />
													</Grid>
													<Grid item={true} sm={6} md={6} lg={6} xl={6}>
														<FedExConfigFormField
															name='smartPostDistributionCenter'
															formikProps={formikProps}
															required={true}
														/>
													</Grid>
												</>
											)}
										</Grid>
									</Grid>

									{/* Tracking ranges */}
									<Grid container={true} className='mt-2'>
										{!isCustomerOrNewAccount && (
											<>
												<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
													<Typography
														noWrap={true}
														color={'inherit'}
														variant='body1'
														className={classNames('display-inline-block')}
													>
														<FormattedMessage
															id='app.carrier-account.FedEx-Native-tracking-ranges-section-header'
															values={{
																edit: (
																	<CustomTooltip
																		items={[
																			{
																				onRender: () => (
																					<Typography variant='body2' color='error' display='inline'>
																						<strong>Not recommended</strong>
																					</Typography>
																				),
																			},
																		]}
																		applyMinWidth={false}
																	>
																		<Link
																			href='#'
																			onClick={(e) => {
																				e.preventDefault();
																				setIsTrackingRangeFieldReadOnly(false);
																			}}
																			variant='body1'
																		>
																			{props.intl.formatMessage({
																				id: 'app.app-view.app-card-fedex-native-edit-text',
																			})}
																		</Link>
																	</CustomTooltip>
																),
															}}
														/>
													</Typography>
												</Grid>
												<Grid container={true} justifyContent='flex-start' alignItems='flex-start' spacing={2}>
													<Grid item={true} xs={6}>
														<FedExConfigFormField
															name='sequenceNumberStart'
															disabled={isCustomer || isTrackingRangeFieldReadOnly}
															type='number'
															formikProps={formikProps}
														/>
													</Grid>
													<Grid item={true} xs={6}>
														<FedExConfigFormField
															name='sequenceNumberEnd'
															disabled={isCustomer || isTrackingRangeFieldReadOnly}
															type='number'
															formikProps={formikProps}
														/>
													</Grid>
													<Grid item={true} xs={6}>
														<FedExConfigFormField
															name='sequenceNumber'
															disabled={isCustomer || isTrackingRangeFieldReadOnly}
															formikProps={formikProps}
															type='number'
														/>
													</Grid>
												</Grid>
											</>
										)}
									</Grid>

									<Grid container={true}>
										<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
											<Typography
												noWrap={true}
												color={'inherit'}
												variant='body1'
												className={classNames('display-inline-block')}
											>
												{props.intl.formatMessage({
													id: 'app.carrier-account.USPS-Alternate-Config-section-header',
												})}
											</Typography>
										</Grid>
										<Grid item={true} sm={6} md={6} lg={6} xl={6}>
											<FedExConfigFormField name='alternateId' formikProps={formikProps} disabled={!IsAdmin} />
										</Grid>
									</Grid>
								</Form>
							);
						}}
					</Formik>

					{!isCustomer && (
						<Grid container={true} direction='column' className='mt-1'>
							<Grid item={true}>
								<Typography variant='body1' color='inherit'>
									<FormattedMessage
										id='app.app-view.app-card-fedex-native-download-tracking-range-help'
										values={{
											link: (
												<Link
													className='pointer'
													onClick={async () => {
														history.push(`/carrier`, {
															carrierId: await fedexNativeCarrierId,
															tab: 3,
														});
													}}
													variant='body1'
												>
													{props.intl.formatMessage({
														id: 'app.app-view.app-card-fedex-native-fuse-link-text',
													})}
												</Link>
											),
										}}
									/>
								</Typography>
							</Grid>
						</Grid>
					)}
				</Grid>
			</Grid>
		);
	}
);

export interface IFormFieldsProps {
	name: string;
	type?: string;
	formikProps: FormikProps<FormikValues>;
	required?: boolean;
	disabled?: boolean;
	labelIntlKey?: string;
}

export default withSnackBar(injectIntl(FedExNativeComponent, { withRef: true }));



// WEBPACK FOOTER //
// ./src/components/carriers/fedex-native.component.tsx